import React, { useState, useEffect } from "react";
import {
  Card,
  FormControlLabel,
  Grid,
  Icon,
  Radio,
  Tooltip,
  TextField,
  MenuItem,
  RadioGroup,
  Select,
  FormControl,
  Autocomplete,
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import MDBadge from "components/MDBadge";
import CenterPopup from "components/CommonComponent/RightPanelDrawer";
import {
  STATUS,
  hasAccess,
  activityTypeOptions,
} from "components/common/constant";
import RequiredLabel from "components/CommonComponent/RequiredLabel";
import { showAlert } from "components/CommonComponent/ShowAlert";
import {
  selectSx,
  radioLabelSx,
  autoCompleteProps,
  wrapCell,
} from "components/CommonComponent/CommonFunction";
import { ArrowDropDown } from "@mui/icons-material";
import MDInput from "components/MDInput";
import { useDispatch, useSelector } from "react-redux";
import { subActivityList } from "constants/subActivity";
import {
  getSubActivityList,
  editSubActivity,
  addSubActivity,
  getSubActivityDetailsById,
  deleteSubActivity,
  importSubActivity,
  exportSubActivityData,
} from "actions/subActivity";
import { useNotify } from "components/CommonComponent/NotificationProvider";
import { getStationList } from "actions/station";
import { stationList } from "constants/station";
import { DEFAULT_RECORDS_PER_PAGE } from "components/common/constant";
import { getDepartmentList } from "actions/department";
import { departmentList } from "constants/department";

const initialFields = {
  id: null,
  department: { value: "", error: "", isChange: false },
  subActivity: {
    value: "",
    error: "",
    isChange: false,
  },
  stationName: {
    value: "",
    error: "",
    isChange: false,
  },
  activityType: {
    value: "",
    error: "",
    isChange: false,
  },
  status: {
    value: "ACTIVE",
    error: "",
    isChange: false,
  },
};

function SubActivityListing() {
  const { notify } = useNotify();
  const dispatch = useDispatch();
  const subActivityData = useSelector(
    (state) => state.subActivity.subActivityList,
  );
  const stationListData = useSelector((state) => state.station.stationList);

  //department data
  const departmentListData = useSelector(
    (state) => state.department.departmentList,
  );
  const authData = JSON.parse(localStorage.getItem("p")) || {};
  const currentRole = authData?.role;

  const canCreate = hasAccess("SUBACTIVITY_MASTER", "CREATE")?.includes(
    currentRole,
  );
  const canUpdate = hasAccess("SUBACTIVITY_MASTER", "UPDATE")?.includes(
    currentRole,
  );
  const canDelete = hasAccess("SUBACTIVITY_MASTER", "DELETE")?.includes(
    currentRole,
  );

  const canShowActions = canUpdate || canDelete;

  const [panelMode, setPanelMode] = useState(null); //add /edit
  const [subActivityFormData, setSubActivityFormData] = useState(initialFields);
  const [isUsed, setIsUsed] = useState(false);
  const [filters, setFilters] = useState({
    subActivity: "",
    departmentName: "",
    activityType: "",
    status: null,
    filterParams: [],
  });
  const [pageNum, setPageNum] = useState(1);
  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });

  const [exportLoading, setExportLoading] = useState(false);

  const departmentOptions = departmentListData?.data?.Department;

  //to filter the station according to department selection
  const selectedDepartmentId = subActivityFormData.department.value?.ID;

  const filteredStations = stationListData?.data?.station?.filter(
    (station) =>
      !selectedDepartmentId || station.department?.ID === selectedDepartmentId,
  );

  //api to fetch sub activity list
  useEffect(() => {
    getSubActivityList(dispatch, pageNum, filters.filterParams);
    return () => {
      dispatch({ type: subActivityList, payload: {} });
    }; //unmount the redux data
  }, [dispatch, pageNum, filters.filterParams]);

  useEffect(() => {
    getStationList(dispatch);
    getDepartmentList(dispatch);
    return () => {
      dispatch({ type: stationList, payload: {} });
      dispatch({ type: departmentList, payload: {} });
    };
  }, [dispatch]);

  //check the listing data state using code and message
  useEffect(() => {
    if (subActivityData?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords: subActivityData?.data?.subActivities?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (subActivityData?.code || subActivityData?.message) {
      setListState({
        isLoading: false,
        error:
          subActivityData?.message ||
          "Failed to load sub-activities list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [subActivityData]);

  const formattedListData = subActivityData?.data?.subActivities?.map(
    (item, index) => ({
      id: item.ID,
      name: item?.name || "-",
      status: item?.status || "-",
      activityType:
        activityTypeOptions.find((type) => type.value === item?.activityType)
          ?.label || "-",
      stationName: item?.station?.name || item?.station || "-",
      departmentName: item?.station?.department?.name || "-",
      srNo:
        (pageNum - 1) *
          (subActivityData?.data?.noOfRecordsPerPage ||
            DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,
    }),
  );

  const handleCsvUpload = async (e) => {
    const file = e.target.files[0];

    if (!file) return;

    // only csv validation
    if (!file.name.toLowerCase().endsWith(".csv")) {
      notify("Please upload a valid CSV file", "error");
      e.target.value = null;
      return;
    }

    try {
      setListState((prev) => ({
        ...prev,
        isLoading: true,
      }));

      const formData = new FormData();
      formData.append("file", file);

      const res = await importSubActivity(formData);

      if (res?.code === 200) {
        notify(
          res?.message || "Sub-Activity CSV imported successfully",
          "success",
        );

        // refresh listing
        getSubActivityList(dispatch, pageNum, filters.filterParams);
      } else {
        const message = Array.isArray(res?.message)
          ? res.message.map((err) => err.Msg || JSON.stringify(err)).join(", ")
          : res?.message || "Failed to import sub-activity CSV";

        notify(message, "error");
      }
    } catch (error) {
      notify(
        error?.response?.data?.message ||
          error?.message ||
          "Something went wrong while importing CSV",
        "error",
      );
    } finally {
      setListState((prev) => ({
        ...prev,
        isLoading: false,
      }));

      // reset input
      e.target.value = null;
    }
  };

  const handleChange = (field, value) => {
    setSubActivityFormData((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        value,
        error: "",
        isChange: true,
      },
    }));
  };

  const getEditedBodyData = (formData) =>
    Object.fromEntries(
      Object.entries(formData)
        .filter(([, field]) => field?.isChange)
        .map(([key, field]) => {
          if (key === "subActivity") return ["name", field.value];
          if (key === "status") return ["status", field.value];
          if (key === "stationName") return ["stationId", field.value?.ID];
          if (key === "activityType")
            return ["activityType", field.value?.value];
          return null;
        })
        .filter(Boolean),
    );

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const isEdit = panelMode === "edit";

    let bodyData = {
      name: subActivityFormData.subActivity.value,
      status: subActivityFormData.status.value,
      stationId: subActivityFormData.stationName.value?.ID,
      activityType: subActivityFormData.activityType.value?.value,
    };

    if (isEdit) {
      const editedData = getEditedBodyData(subActivityFormData);
      if (Object.keys(editedData).length === 0) {
        notify("No changes detected", "info");
        return;
      }
      bodyData = editedData;
    }
    try {
      setListState((prev) => ({
        ...prev,
        showLoaderOnClick: true,
      }));

      const res = isEdit
        ? await editSubActivity(subActivityFormData.id, bodyData)
        : await addSubActivity(bodyData);

      if (res?.code === 200) {
        notify(
          res?.message ||
            `Sub-Activity ${isEdit ? "updated" : "added"} successfully`,
          "success",
        );
        closeModal();
        // pageNum === 1
           getSubActivityList(dispatch, pageNum, filters.filterParams)
          // : setPageNum(1);
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message || "Failed to save sub activity";

        notify(message, "error");
      }
    } catch (error) {
      notify(`Failed to ${isEdit ? "update" : "add"} sub-activity`, "error");
    } finally {
      setListState((prev) => ({
        ...prev,
        showLoaderOnClick: false,
      }));
    }
  };

  const closeModal = () => {
    setPanelMode(null);
    setIsUsed(false);
    setSubActivityFormData({
      ...initialFields,
    });
  };

  const getValidationError = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    switch (name) {
      case "subActivity":
        return !trimmedValue ? "Please enter sub-activity" : "";

      case "stationName":
        return !value || !value.ID ? "Please select station" : "";

      case "activityType":
        return !value?.value ? "Please select activity type" : "";

      default:
        return "";
    }
  };

  //on blur to get if any validation error exits
  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);
    setSubActivityFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmedValue,
        error,
      },
    }));
  };

  const validateForm = () => {
    let newData = { ...subActivityFormData };
    Object.keys(newData).forEach((key) => {
      if (key === "id") return;
      newData[key] = {
        ...newData[key],
        error: getValidationError(key, newData[key].value),
      };
    });
    setSubActivityFormData(newData);
    return Object.values(newData)
      .filter((item, i) => Object.keys(newData)[i] !== "id")
      .every((item) => item.error === "");
  };

  const openAddModal = () => {
    // e.stopPropagation();
    setPanelMode("add");
    setIsUsed(false);
    setSubActivityFormData(initialFields);
  };

  const openEditModal = async (row) => {
    setPanelMode("edit");
    setListState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    try {
      const res = await getSubActivityDetailsById(row.id);
      if (res?.code === 200) {
        const data = res.data;
        setIsUsed(data?.isUsed ?? false);
        const selectedStation =
          stationListData?.data?.station?.find(
            (s) => s.ID === data?.station?.ID,
          ) || null;
        const selectedDepartment = data?.station?.department || null;
        setSubActivityFormData({
          id: data.ID,
          subActivity: {
            value: data.name || "",
            error: "",
            isChange: false,
          },
          stationName: {
            value: selectedStation,
            error: "",
            isChange: false,
          },
          department: {
            value: selectedDepartment,
            error: "",
            isChange: false,
          },
          status: {
            value: data.status || "ACTIVE",
            error: "",
            isChange: false,
          },
          activityType: {
            value:
              activityTypeOptions.find(
                (item) => item.value === data.activityType,
              ) || null,
            error: "",
            isChange: false,
          },
        });
      } else {
        notify(res?.message || "Failed to fetch details", "error");
        closeModal();
      }
    } finally {
      setListState((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  const handleDelete = (row) => {
    showAlert({
      title: `Are you sure you want to delete this sub-activity?`,
      message: `Sub-Activity "${row.name}" will be deleted permanently.`,
      type: "warning",
      showCancel: true,
      confirmText: "Yes, delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          setListState((prev) => ({
            ...prev,
            isLoading: true,
          }));
          const res = await deleteSubActivity(row.id);

          if (res?.code === 200) {
            notify(
              res?.message || "Sub-activity deleted successfully",
              "success",
            );
            pageNum === 1
              ? getSubActivityList(dispatch, pageNum, filters.filterParams)
              : setPageNum(1);
          } else {
            notify(res?.message || "Failed to delete sub-activity", "error");
          }
        } catch (error) {
          notify("Something went wrong while deleting sub-activity", "error");
        } finally {
          setListState((prev) => ({
            ...prev,
            isLoading: false,
          }));
        }
      },
      onCancel: () => {},
    });
  };

  const renderStatusBadge = (status) => {
    if (!status) return null;
    return (
      <MDBadge
        badgeContent={status}
        color={status === "ACTIVE" ? "success" : "secondary"}
        variant="gradient"
        size="sm"
        circular
      />
    );
  };

  // const wrapCell = (value) => (
  //   <div
  //     style={{
  //       whiteSpace: "normal",
  //       wordBreak: "break-word",
  //       overflowWrap: "break-word",
  //       lineHeight: "1.5",
  //       maxWidth: "250px",
  //     }}
  //   >
  //     {value || "-"}
  //   </div>
  // );

  const columns = [
    {
      Header: "Sr No.",
      accessor: "srNo",
      align: "left",
      Cell: ({ value }) => wrapCell(value),
    },
    {
      Header: "Department",
      accessor: "departmentName",
      width: 170,
      align: "left",
      Cell: ({ value }) => wrapCell(value),
    },
    {
      Header: "Station",
      accessor: "stationName",
      align: "left",
      width: 160,
      Cell: ({ value }) => wrapCell(value),
    },
    {
      Header: "Activity Type",
      accessor: "activityType",
      align: "left",
      width: 180,
      Cell: (cell) => cell.value || "-",
    },
    {
      Header: "Sub Activity",
      accessor: "name",
      align: "left",
      width: 170,
      Cell: ({ value }) => wrapCell(value, "13.5rem"),
    },
    {
      Header: "Status",
      accessor: "status",
      align: "center",
      Cell: (cell) => renderStatusBadge(cell.row.original.status),
    },
    ...(canShowActions
      ? [
          {
            Header: "Action",
            accessor: "actions",
            align: "center",
            disableSortBy: true,
            Cell: (cell) => {
              const rowData = cell.row.original;

              return (
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gap={1}
                >
                  {canUpdate && (
                    <Tooltip title="Edit" arrow>
                      <Icon
                        sx={({ palette }) => ({
                          cursor: "pointer",
                          color: palette.secondary.main,
                        })}
                        onClick={() => openEditModal(rowData)}
                      >
                        <i className="fas fa-edit fa-sm" />
                      </Icon>
                    </Tooltip>
                  )}

                  {canUpdate && canDelete && (
                    <MDTypography variant="caption" mx={0.5}>
                      |
                    </MDTypography>
                  )}

                  {canDelete && (
                    <Tooltip title="Delete" arrow>
                      <Icon
                        color="error"
                        sx={{
                          cursor: "pointer",
                          opacity: 0.5,
                          "&:hover": { opacity: 1 },
                        }}
                        onClick={() => handleDelete(rowData)}
                      >
                        <i className="fas fa-trash" />
                      </Icon>
                    </Tooltip>
                  )}
                </MDBox>
              );
            },
          },
        ]
      : []),
  ];

  // filters for search
  const buildFilterParams = () => {
    const filterParams = [];

    if (filters.department) {
      filterParams.push({
        field: "Department",
        condition: "eq",
        filterValues: [String(filters.department)],
      });
    }

    if (filters.activityType) {
      filterParams.push({
        field: "ActivityType",
        condition: "eq",
        filterValues: [filters.activityType],
      });
    }

    if (filters.subActivity?.trim()) {
      filterParams.push({
        field: "Name",
        condition: "like",
        filterValues: [filters.subActivity.trim()],
      });
    }

    if (filters.station) {
      filterParams.push({
        field: "StationID",
        condition: "eq",
        filterValues: [String(filters.station)],
      });
    }

    if (filters.status) {
      filterParams.push({
        field: "Status",
        condition: "eq",
        filterValues: [filters.status],
      });
    }
    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  const handleSearch = () => {
    const newfilterParams = buildFilterParams();

    if (
      JSON.stringify(filters.filterParams) !== JSON.stringify(newfilterParams)
    ) {
      setListState((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));
      setFilters((prev) => ({
        ...prev,
        filterParams: newfilterParams,
      }));

      if (pageNum !== 1) setPageNum(1);
    }
  };

  const downloadSubActivityCSV = async () => {
    try {
      setExportLoading(true);

      const res = await exportSubActivityData(filters.filterParams);
      
      const url = window.URL.createObjectURL(res.data);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "SubActivity.csv");

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      notify(
        res?.message || "SubActivity file downloaded successfully",
        "success",
      );
    } catch (error) {
      notify("Failed to download SubActivity file", "error");
    } finally {
      setExportLoading(false);
    }
  };

  const downloadSampleCsv = () => {
    const csvContent = `ID,Department Name,Station Name,SubActivity Name
1,Winding,WDG-05,Wiring Check
2,Dispatch,DISP-01,Valve Inspection
3,Tanking,TP-01,Battery Testing`;

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", "sample_subactivity.csv");
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClear = () => {
    setFilters({
      subActivity: "",
      status: null,
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };
  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox width="100%">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ width: "100%" }}>
              <MDBox
                px={3}
                pt={3}
                pb={2}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h5" fontWeight="bold">
                  Sub-Activity
                </MDTypography>

                {canCreate && (
                  <MDBox display="flex" gap={2}>
                    <MDButton
                      variant="gradient"
                      color="light"
                      startIcon={<Icon>file_download</Icon>}
                      sx={{
                        py: 1.2,
                        px: 3,
                        fontWeight: "bold",
                        textTransform: "none",
                      }}
                      onClick={downloadSampleCsv}
                    >
                      Sample CSV
                    </MDButton>

                    <MDButton
                      variant="gradient"
                      color="light"
                      startIcon={<Icon>file_upload</Icon>}
                      sx={{
                        py: 1.2,
                        px: 3,
                        fontWeight: "bold",
                        textTransform: "none",
                      }}
                      onClick={downloadSubActivityCSV}
                    >
                      {exportLoading ? (
                        <>
                          <i className="fa fa-spinner fa-spin mr-2" />
                          Exporting...
                        </>
                      ) : (
                        "Export Sub Activity"
                      )}
                    </MDButton>
                    <MDButton
                      variant="gradient"
                      color="light"
                      component="label"
                      startIcon={<Icon>download</Icon>}
                      sx={{
                        py: 1.2,
                        px: 3,
                        fontWeight: "bold",
                        textTransform: "none",
                      }}
                    >
                      Import CSV
                      <input
                        type="file"
                        hidden
                        accept=".csv"
                        onChange={handleCsvUpload}
                      />
                    </MDButton>

                    <MDButton
                      type="button"
                      variant="contained"
                      color="deepBlue"
                      sx={{
                        py: 1.2,
                        px: 3,
                        fontWeight: "bold",
                        fontSize: "0.9rem",
                        textTransform: "none",
                      }}
                      onClick={openAddModal}
                    >
                      Add Sub-Activity
                    </MDButton>
                  </MDBox>
                )}
              </MDBox>

              {/* Filters */}
              <MDBox px={3} pb={2} onKeyDown={handleKeyDown}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal={true}
                      size="small"
                      options={departmentOptions || []}
                      getOptionLabel={(option) => option.name || ""}
                      value={
                        departmentOptions?.find(
                          (dept) => dept.ID === filters.department,
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        setFilters({
                          ...filters,
                          department: newValue?.ID || "",
                          station: "",
                        })
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.ID === value?.ID
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select department"
                        />
                      )}
                    />
                  </Grid>
                  {/* station Filter */}
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal={true}
                      size="small"
                      options={stationListData?.data?.station || []}
                      getOptionLabel={(option) => option.name || ""}
                      value={
                        stationListData?.data?.station?.find(
                          (stat) => stat.ID === filters.station,
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        setFilters({
                          ...filters,
                          station: newValue?.ID || "",
                        })
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.ID === value?.ID
                      }
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select station" />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal={true}
                      size="small"
                      options={activityTypeOptions || []}
                      getOptionLabel={(option) => option.label || ""}
                      value={
                        activityTypeOptions?.find(
                          (type) => type.value === filters.activityType,
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        setFilters({
                          ...filters,
                          activityType: newValue?.value || "",
                        })
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.value === value?.value
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select activity type"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <TextField
                      fullWidth
                      placeholder="Search by sub-activity "
                      size="small"
                      value={filters.subActivity}
                      onChange={(e) =>
                        setFilters({ ...filters, subActivity: e.target.value })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <FormControl fullWidth size="small" sx={selectSx("100%")}>
                      <Select
                        value={filters.status || ""}
                        displayEmpty
                        onChange={(e) =>
                          setFilters({ ...filters, status: e.target.value })
                        }
                        IconComponent={ArrowDropDown}
                        renderValue={(selected) => {
                          if (!selected)
                            return (
                              <span style={{ color: "rgba(0, 0, 0, 0.38)" }}>
                                 Select Status
                              </span>
                            );

                          return renderStatusBadge(selected);
                        }}
                      >
                        {STATUS.map((status) => (
                          <MenuItem key={status.label} value={status.label}>
                            {renderStatusBadge(status.label)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                    <MDBox
                      display="flex"
                      gap={1}
                      flexWrap="nowrap"
                      width="100%"
                    >
                      <MDButton
                        variant="contained"
                        color="deepBlue"
                        sx={{
                          flex: 1,
                          minWidth: "90px",
                          maxWidth: "160px",
                          whiteSpace: "nowrap",
                        }}
                        onClick={handleSearch}
                        disabled={listState.showLoaderOnClick}
                      >
                        {listState.showLoaderOnClick ? (
                          <i className="fa fa-spinner fa-spin fa-lg text-white" />
                        ) : (
                          <>
                            <Icon sx={{ mr: 1 }}>search</Icon>
                            Search
                          </>
                        )}
                      </MDButton>
                      {filters.filterParams.length > 0 && (
                        <MDButton
                          variant="contained"
                          color="deepBlue"
                          sx={{
                            flex: 1,
                            minWidth: "90px",
                            maxWidth: "160px",
                            whiteSpace: "nowrap",
                          }}
                          onClick={handleClear}
                        >
                          <Icon sx={{ mr: 1 }}>close</Icon>
                          Clear
                        </MDButton>
                      )}
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>

              <MDBox
                px={3}
                py={1}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="body1" color="deepBlue" mb={1}>
                  Total Records: {subActivityData?.data?.totalRecords || 0}
                </MDTypography>
              </MDBox>

              <MDBox sx={{ width: "100%", overflowX: "auto" }}>
                {listState.isLoading ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </MDBox>
                ) : listState.error ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <MDTypography color="error" variant="body2">
                      {listState.error}
                    </MDTypography>
                  </MDBox>
                ) : listState.noRecords ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="20vh"
                  >
                    <MDTypography color="text" variant="body2">
                      No sub-activity found.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    columns={columns}
                    data={formattedListData || []}
                    isSorted
                    pageNum={pageNum}
                    setPageNum={setPageNum}
                    totalPages={Math.ceil(
                      (subActivityData?.data?.totalRecords || 0) /
                        (subActivityData?.data?.noOfRecordsPerPage ||
                          DEFAULT_RECORDS_PER_PAGE),
                    )}
                  />
                )}
              </MDBox>
              <MDBox sx={{ width: "100%", overflowX: "auto" }}></MDBox>
            </Card>
          </Grid>
        </Grid>

        <CenterPopup isOpen={panelMode !== null} onClose={closeModal}>
          <MDBox sx={{ p: 2 }}>
            <MDTypography variant="h6" mb={0}>
              {panelMode === "edit" ? "Edit Sub-Activity" : "Add Sub-Activity"}
            </MDTypography>

            {panelMode === "edit" && isUsed && (
              <Grid item xs={12} sx={{ mb: 1 }}>
                <MDTypography
                  variant="caption"
                  sx={{
                    color: "#fc6e0f",
                  }}
                >
                  <b>Note:</b> This sub-activity is used in timesheets, so
                  Department, Station, Activity Type and Sub-Activity cannot be
                  edited.
                </MDTypography>
              </Grid>
            )}

            {listState.isLoading ? (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="150px"
              >
                <i className="fa fa-spinner fa-spin fa-2xl loader" />
              </MDBox>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <RequiredLabel required>Department</RequiredLabel>
                  <Autocomplete
                    {...autoCompleteProps}
                    size="small"
                    disabled={panelMode === "edit" && isUsed}
                    options={departmentOptions || []}
                    getOptionLabel={(o) => o.name || ""}
                    value={subActivityFormData.department.value}
                    onChange={(event, newValue) => {
                      setSubActivityFormData((prev) => ({
                        ...prev,
                        department: {
                          value: newValue || null,
                          error: "",
                          isChange: true,
                        },
                        stationName: {
                          value: null,
                          error: "",
                          isChange: true,
                        },
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Select Department" />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <RequiredLabel required>Station</RequiredLabel>
                  <Autocomplete
                    {...autoCompleteProps}
                    size="small"
                    disablePortal
                    disabled={
                      !subActivityFormData.department.value ||
                      (panelMode === "edit" && isUsed)
                    }
                    options={filteredStations || []}
                    getOptionLabel={(option) =>
                      option ? `${option.name}` : ""
                    }
                    value={subActivityFormData.stationName.value}
                    onChange={(event, newValue) => {
                      setSubActivityFormData((prev) => ({
                        ...prev,
                        stationName: {
                          value: newValue || null,
                          error: "",
                          isChange: true,
                        },
                      }));
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option?.ID === value?.ID
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select station"
                        error={!!subActivityFormData.stationName.error}
                        helperText={subActivityFormData.stationName.error}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <RequiredLabel required>Activity Type</RequiredLabel>
                  <Autocomplete
                    {...autoCompleteProps}
                    size="small"
                    disabled={panelMode === "edit" && isUsed}
                    options={activityTypeOptions}
                    getOptionLabel={(option) => option.label || ""}
                    value={subActivityFormData.activityType.value}
                    onChange={(event, newValue) => {
                      setSubActivityFormData((prev) => ({
                        ...prev,
                        activityType: {
                          value: newValue || null,
                          error: "",
                          isChange: true,
                        },
                      }));
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option?.value === value?.value
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select activity type"
                        error={!!subActivityFormData.activityType.error}
                        helperText={subActivityFormData.activityType.error}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <RequiredLabel required>Sub-Activity</RequiredLabel>
                  <MDInput
                    variant="outlined"
                    type="text"
                    name="subActivity"
                    placeholder="Enter sub-activity"
                    value={subActivityFormData.subActivity.value}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    error={!!subActivityFormData.subActivity.error}
                    fullWidth
                    disabled={panelMode === "edit" && isUsed}
                    inputProps={{ maxLength: 100 }}
                  />
                  {subActivityFormData.subActivity.error && (
                    <MDTypography variant="caption" color="error">
                      {subActivityFormData.subActivity.error}
                    </MDTypography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <RequiredLabel>Status</RequiredLabel>
                  <RadioGroup
                    row
                    name="status"
                    value={subActivityFormData.status.value}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                  >
                    <FormControlLabel
                      value="ACTIVE"
                      control={<Radio size="small" />}
                      label="Active"
                      sx={radioLabelSx}
                    />
                    <FormControlLabel
                      value="INACTIVE"
                      control={<Radio size="small" />}
                      label="Inactive"
                      sx={radioLabelSx}
                    />
                  </RadioGroup>
                </Grid>

                {/* Save Button */}
                <Grid item xs={12}>
                  <MDBox display="flex" justifyContent="flex-end">
                    <MDButton
                      variant="gradient"
                      color="light"
                      onClick={closeModal}
                      sx={{ mr: 2 }}
                    >
                      Cancel
                    </MDButton>

                    <MDButton
                      type="submit"
                      variant="contained"
                      color="deepBlue"
                      onClick={handleSubmit}
                    >
                      {listState.showLoaderOnClick ? (
                        <>
                          <i className="fa fa-spinner fa-spin fa-lg text-white" />
                        </>
                      ) : (
                        "Save"
                      )}
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            )}
          </MDBox>
        </CenterPopup>
      </MDBox>
    </DashboardLayout>
  );
}

export default SubActivityListing;
